import { FC, useEffect, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Dayjs } from "dayjs";
import { TaskCounterRow } from "./TaskCounterRow";
import {
  TaskCounterLocalRow,
  TaskCounterUserTemplate,
} from "../types/TaskCounterTypes";
import { useTaskCounterTemplates } from "../hooks/useTaskCounterTemplates";

type Props = {
  rows: TaskCounterLocalRow[];
  selectedDate: Dayjs;
  updateRow: (localId: string, updates: Partial<TaskCounterLocalRow>) => void;
  removeRow: (localId: string) => void;
  focusId: number | null;
};

export const TaskCounterGrid: FC<Props> = ({
  rows,
  selectedDate,
  updateRow,
  removeRow,
  focusId,
}) => {
  const templates: TaskCounterUserTemplate[] = useTaskCounterTemplates();
  const focusRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusId, rows]);

  // Sort: unsaved rows first, then saved rows by lastModifiedTime desc
  const sortedRows = [...rows].sort((a, b) => {
    if (a.lastModifiedTime && b.lastModifiedTime) {
      return new Date(b.lastModifiedTime).getTime() - new Date(a.lastModifiedTime).getTime();
    }
    if (a.lastModifiedTime && !b.lastModifiedTime) return 1;
    if (!a.lastModifiedTime && b.lastModifiedTime) return -1;
    return 0;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 50px)", maxHeight: 600 }}>
      <TableContainer
        component={Paper}
        sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}
      >
        <Table aria-label="task counter grid">
          <TableHead sx={{ backgroundColor: "#F5F4F4" }}>
            <TableRow>
              <TableCell sx={{minWidth: 270, fontWeight: 600 }}>Task Name</TableCell>
              <TableCell sx={{minWidth: 50, fontWeight: 600 }}>Value</TableCell>
              <TableCell sx={{minWidth: 150, fontWeight: 600 }}>Unit of Measure</TableCell>
              <TableCell sx={{minWidth: 300, fontWeight: 600 }}>Comment</TableCell>
              <TableCell sx={{minWidth: 175, fontWeight: 600 }}>Last Modified</TableCell>
              <TableCell sx={{minWidth: 80, fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
        <TableBody>
          {sortedRows.map((row) => (
            <TaskCounterRow
              key={row.localId}
              row={row}
              templates={templates}
              selectedDate={selectedDate}
              updateRow={updateRow}
              removeRow={removeRow}
              ref={row.persistedId === focusId ? focusRef : undefined}
            />
          ))}
          {sortedRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, border: "none" }}>
                No tasks for this date. Click "Add New Task" to start.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
